import { useAuth0 } from '@auth0/auth0-react';
import { Close } from '@zappyrent/hundop';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  StyledCloseButton,
  StyledConfirmButton,
  StyledEditButton,
  StyledLogoutButton,
  StyledProfile,
  StyledProfileBottomBar,
  StyledProfileData,
  StyledProfileDataFields,
  StyledProfileTopBar,
} from './Profile.styles';
import { IProfileProps } from './Profile.types';
import referrerApi from '../../api/referrer/referrer';
import InputField from '../UI/InputField';
import { StyledH3 } from '../UI/Typography';

const Profile = ({
  onCloseBtnClick,
  profileData,
}: IProfileProps): JSX.Element => {
  const { t } = useTranslation(['common']);
  const { locale } = useParams();
  const { logout } = useAuth0();

  const [isEditingProfileData, setIsEditingProfileData] = useState(false);

  const [firstName, setFirstName] = useState({
    value: profileData?.firstName ?? '',
    initValue: profileData?.firstName ?? '',
  });
  const [lastName, setLastName] = useState({
    value: profileData?.lastName ?? '',
    initValue: profileData?.lastName ?? '',
  });
  const [birthDate, setBirthDate] = useState({
    value: profileData?.birthDate ?? '',
    initValue: profileData?.birthDate ?? '',
  });
  const [address, setAddress] = useState({
    value: profileData?.address ?? '',
    initValue: profileData?.address ?? '',
  });
  const [phoneNumber, setPhoneNumber] = useState({
    value: profileData?.phoneNumber ?? '',
    initValue: profileData?.phoneNumber ?? '',
  });
  const email = profileData?.email ?? '';
  const [iban, setIban] = useState({
    value: profileData?.iban ?? '',
    initValue: profileData?.iban ?? '',
  });

  const changeIsEditingProfileData = () => {
    setIsEditingProfileData(prevValue => !prevValue);
  };

  const saveInputFieldsChanges = () => {
    setFirstName(prevValue => ({ ...prevValue, initValue: prevValue.value }));
    setLastName(prevValue => ({ ...prevValue, initValue: prevValue.value }));
    setBirthDate(prevValue => ({ ...prevValue, initValue: prevValue.value }));
    setAddress(prevValue => ({ ...prevValue, initValue: prevValue.value }));
    setPhoneNumber(prevValue => ({ ...prevValue, initValue: prevValue.value }));
    setIban(prevValue => ({ ...prevValue, initValue: prevValue.value }));

    const updateReferrerFromApi = async () => {
      try {
        await referrerApi.updateReferrer({
          first_name: firstName.value !== '' ? firstName.value : null,
          last_name: lastName.value !== '' ? lastName.value : null,
          birth_date: birthDate.value !== '' ? new Date(birthDate.value) : null,
          address: address.value !== '' ? address.value : null,
          phone_no: phoneNumber.value !== '' ? phoneNumber.value : null,
          email,
          iban: iban.value !== '' ? iban.value : null,
        });
      } catch (e) {
        console.error(e);
      }
    };

    updateReferrerFromApi();
    changeIsEditingProfileData();
  };

  const resetInputFieldsChanges = () => {
    setFirstName(prevValue => ({ ...prevValue, value: prevValue.initValue }));
    setLastName(prevValue => ({ ...prevValue, value: prevValue.initValue }));
    setBirthDate(prevValue => ({ ...prevValue, value: prevValue.initValue }));
    setAddress(prevValue => ({ ...prevValue, value: prevValue.initValue }));
    setPhoneNumber(prevValue => ({ ...prevValue, value: prevValue.initValue }));
    setIban(prevValue => ({ ...prevValue, value: prevValue.initValue }));

    changeIsEditingProfileData();
  };

  const handleLogoutBtnClick = () => {
    logout({
      returnTo: `${process.env.REACT_APP_SITE_URL}/${locale}/refer-a-landlord`,
    });
  };

  return (
    <StyledProfile
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
    >
      <StyledProfileTopBar>
        <StyledCloseButton onClick={onCloseBtnClick}>
          <Close />
        </StyledCloseButton>
      </StyledProfileTopBar>
      <StyledProfileData>
        <div>
          <StyledH3>{t('common:personalInformation')}</StyledH3>
          <StyledEditButton
            onClick={
              isEditingProfileData
                ? resetInputFieldsChanges
                : changeIsEditingProfileData
            }
          >
            {isEditingProfileData ? t('common:cancel') : t('common:edit')}
          </StyledEditButton>
        </div>
        <StyledProfileDataFields>
          <InputField
            label={t('common:name')}
            value={firstName.value}
            setValue={setFirstName}
            disabled={!isEditingProfileData}
            placeholder="Mario"
            type="text"
          />
          <InputField
            label={t('common:surname')}
            value={lastName.value}
            setValue={setLastName}
            disabled={!isEditingProfileData}
            placeholder="Rossi"
            type="text"
          />
          <InputField
            label={t('common:birthDate')}
            value={birthDate.value}
            setValue={setBirthDate}
            disabled={!isEditingProfileData}
            placeholder="dd/mm/yyyy"
            type="date"
          />
          <InputField
            label={t('common:address')}
            value={address.value}
            setValue={setAddress}
            disabled={!isEditingProfileData}
            placeholder="Piazza Italia 10, 20121, Milano, MI"
            type="text"
          />
          <InputField
            label={t('common:phoneNumber')}
            value={phoneNumber.value}
            setValue={setPhoneNumber}
            disabled={!isEditingProfileData}
            placeholder="+39 xxx xxxxxx"
            type="tel"
          />
          <InputField
            label={t('common:email')}
            value={email}
            disabled={true}
            placeholder="mario.rossi@gmail.com"
            type="email"
          />
          <InputField
            label={t('common:iban')}
            value={iban.value}
            setValue={setIban}
            disabled={!isEditingProfileData}
            placeholder="IT60X0542811101000000123456"
            type="text"
          />
        </StyledProfileDataFields>
      </StyledProfileData>
      <StyledProfileBottomBar>
        {isEditingProfileData && (
          <StyledConfirmButton
            variant="primary"
            user="landlord"
            onClick={saveInputFieldsChanges}
          >
            {t('common:confirm')}
          </StyledConfirmButton>
        )}
        <StyledLogoutButton
          variant="secondary"
          user="landlord"
          onClick={handleLogoutBtnClick}
        >
          {t('common:logout')}
        </StyledLogoutButton>
      </StyledProfileBottomBar>
    </StyledProfile>
  );
};

export default Profile;
